import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { config } from '../config/config'
import type { User } from '../models'

const USER_KV_KEY = 'user'

export type UserStore = {
  user: User | null
  persist: boolean
  /** Set only when authentication can no longer be recovered by refreshing tokens. */
  sessionExpired: boolean
  onRefreshTokenError?: (error: unknown) => void
  onInitError?: (error: unknown) => void
  onSignInError?: (error: unknown) => void
}

export const useUserStore = create<UserStore>()(
  immer(() => ({
    user: null,
    persist: true,
    sessionExpired: false,
  })),
)

function assignUser(user: User | null) {
  useUserStore.setState((draft) => {
    draft.user = user
    if (user) {
      draft.sessionExpired = false
    }
  })
}

function parseStoredUser(raw: string | null): User | null {
  if (!raw) return null
  return JSON.parse(raw) as User | null
}

function isSameUser(left: User | null, right: User | null): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

let stopWebSync: (() => void) | null = null

function isWebUserSessionSyncEnabled(): boolean {
  return (
    config.getAccountType() === 'web' &&
    typeof window !== 'undefined' &&
    typeof localStorage !== 'undefined'
  )
}

function applyUserFromOtherTab(raw: string | null) {
  try {
    const user = parseStoredUser(raw)
    if (isSameUser(useUserStore.getState().user, user)) return
    assignUser(user)
  } catch (error) {
    useUserStore.getState().onInitError?.(error)
  }
}

function startWebUserSessionSync() {
  if (!isWebUserSessionSyncEnabled() || stopWebSync) return

  const onStorage = (event: StorageEvent) => {
    if (event.key !== USER_KV_KEY) return
    if (event.storageArea && event.storageArea !== localStorage) return
    applyUserFromOtherTab(event.newValue)
  }

  window.addEventListener('storage', onStorage)
  stopWebSync = () => {
    window.removeEventListener('storage', onStorage)
    stopWebSync = null
  }
}

export function stopWebUserSessionSync() {
  stopWebSync?.()
}

export async function setUser(user: User | null) {
  assignUser(user)

  if (useUserStore.getState().persist) {
    await config.getSaveKv()(USER_KV_KEY, JSON.stringify(user))
  }

  return user
}

/**
 * Ends a session because its credentials can no longer be refreshed.
 *
 * This deliberately differs from `setUser(null)`: a user-initiated logout
 * should clear credentials without showing the session-expired experience.
 */
export async function expireUserSession(): Promise<void> {
  if (useUserStore.getState().sessionExpired) return

  useUserStore.setState((draft) => {
    draft.user = null
    draft.sessionExpired = true
  })

  if (useUserStore.getState().persist) {
    await config.getSaveKv()(USER_KV_KEY, JSON.stringify(null))
  }
}

export async function initUserStore() {
  if (!useUserStore.getState().persist) return

  const user = await config.getLoadKv()(USER_KV_KEY)
  if (user) {
    try {
      await setUser(JSON.parse(user) as User | null)
    } catch (error) {
      useUserStore.getState().onInitError?.(error)
    }
  }

  startWebUserSessionSync()
}

export function useUser() {
  return useUserStore((v) => v.user)
}
